/**
 * Small deterministic PRNG (mulberry32) so game setup/shuffles can be seeded
 * and replayed/tested. Not cryptographically secure - fine for shuffling a
 * card game, not for anything security-sensitive (auth tokens use crypto RNG
 * elsewhere).
 */
export interface RNG {
  /** Returns a float in [0, 1). */
  next(): number;
  /** Returns an integer in [0, maxExclusive). */
  nextInt(maxExclusive: number): number;
  seed: number;
  /**
   * How many values have been drawn so far. Together with `seed` this is the
   * generator's entire state, so a saved round can resume with the exact same
   * sequence of AI decisions and timeout moves it would have had otherwise.
   */
  readonly steps: number;
}

/**
 * Creates a seeded generator, optionally fast-forwarded past `steps` values.
 * Fast-forwarding is O(steps) but only ever runs once, on resuming a saved
 * round, where steps is at most a few thousand.
 */
export function makeRng(seed: number, steps = 0): RNG {
  let a = seed >>> 0;
  let count = 0;
  const next = () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    count++;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = 0; i < steps; i++) next();
  count = steps;
  return {
    seed,
    next,
    nextInt(maxExclusive: number) {
      return Math.floor(next() * maxExclusive);
    },
    get steps() {
      return count;
    },
  };
}

export function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

export function shuffle<T>(items: T[], rng: RNG): T[] {
  const arr = items.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}
